import React, { useState, useEffect, useMemo } from 'react';
import {
  Table,
  Button,
  Card,
  Input,
  DatePicker,
  Space,
  Typography,
  message,
  Tag,
  Tooltip,
  Row,
  Col,
  Statistic,
  Radio,
  Select,
  Divider,
  Badge,
  Empty,
  Spin,
  Alert,
  Progress,
  Modal,
  Tabs,
  Descriptions,
  List,
  Avatar,
  Segmented
} from 'antd';
import {
  SearchOutlined,
  ReloadOutlined,
  PrinterOutlined,
  ExportOutlined,
  HomeOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  EyeOutlined,
  FileTextOutlined,
  BarChartOutlined,
  UnorderedListOutlined,
  TableOutlined,
  PercentageOutlined,
  WarningOutlined,
  InfoCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import axios from 'axios';

import PageContainer from '../components/PageContainer';
import { BASE_URL } from '../utils/constants';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const HabitacionesLibres = () => {
  // Estados principales
  const [habitaciones, setHabitaciones] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [filtroTipo, setFiltroTipo] = useState('TODAS');
  const [vista, setVista] = useState('tabla');
  const [estadisticas, setEstadisticas] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [modalVisible, setModalVisible] = useState(false);

  const [rangoMinMax, setRangoMinMax] = useState({ minimo: null, maximo: null });  
  
  // Estados para filtros
  const [fechas, setFechas] = useState({
    desde: dayjs(),
    hasta: dayjs().add(1, 'days')
  });
  const [rangoHabs, setRangoHabs] = useState({
    desde: '',
    hasta: ''
  });
  const [tipoHabitacion, setTipoHabitacion] = useState('');
  
  // Estados para resumen
  const [resumenTipos, setResumenTipos] = useState([]);
  const [ocupacion, setOcupacion] = useState(0);
  
  // Configuración de columnas de la tabla
  const columns2 = [
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      fixed: 'left',
      sorter: (a, b) => a.NumHab - b.NumHab,
      render: (numHab) => (
        <Tag 
          color="blue" 
          style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            padding: '4px 12px',
            borderRadius: '20px'
          }}
        >
          <HomeOutlined style={{ marginRight: 6 }} />
          {numHab}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': { label: 'Individual', color: 'cyan', icon: '👤' },
          'DO': { label: 'Doble', color: 'green', icon: '👥' },
          'TR': { label: 'Triple', color: 'orange', icon: '👥👤' },
          'CU': { label: 'Cuádruple', color: 'purple', icon: '👥👥' },
          'QU': { label: 'Quíntuple', color: 'red', icon: '👥👥👤' }
        };
        const config = tipos[tipo] || { label: tipo, color: 'default', icon: '🏠' };
        return (
          <Tag color={config.color} style={{ padding: '4px 10px', borderRadius: '16px' }}>
            {config.icon} {config.label}
          </Tag>
        );
      },
      filters: [
        { text: 'Individuales', value: 'IN' },
        { text: 'Dobles', value: 'DO' },
        { text: 'Triples', value: 'TR' },
        { text: 'Cuádruples', value: 'CU' },
        { text: 'Quíntuples', value: 'QU' }
      ],
      onFilter: (value, record) => record.TipoHab === value
    },
    {
      title: 'SubTipo',
      dataIndex: 'SubTipo',
      key: 'SubTipo',
      width: 120,
      render: (subtipo) => subtipo || '—'
    },
    {
      title: 'Desde',
      dataIndex: 'Desde',
      key: 'Desde',
      width: 120,
      render: (desde) => desde ? dayjs(desde).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Desde).unix() - dayjs(b.Desde).unix()
    },
    {
      title: 'Hasta',
      dataIndex: 'Hasta',
      key: 'Hasta',
      width: 120,
      render: (hasta) => hasta ? dayjs(hasta).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Hasta).unix() - dayjs(b.Hasta).unix()
    },
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 110,
      align: 'center',
      sorter: (a, b) => a.Dias - b.Dias,
      render: (dias) => (
        <Badge 
          count={dias} 
          style={{ 
            backgroundColor: dias > 30 ? '#52c41a' : dias > 7 ? '#1890ff' : '#faad14',
            borderRadius: '12px',
            padding: '0 8px'
          }}
          showZero
        />
      )
    },
    {
      title: 'Estado',
      key: 'estado',
      width: 100,
      align: 'center',
      render: (_, record) => {
        const hoy = dayjs();
        const desde = dayjs(record.Desde);
        const hasta = dayjs(record.Hasta);
        
        if (desde.isAfter(hoy)) {
          return <Tag color="processing" icon={<InfoCircleOutlined />}>Próxima</Tag>;
        } else if (hasta.isBefore(hoy)) {
          return <Tag color="default">Pasada</Tag>;
        } else {
          return <Tag color="success" icon={<CheckCircleOutlined />}>Disponible</Tag>;
        }
      }
    },
    // {
    //   title: 'Acciones',
    //   key: 'actions',
    //   width: 100,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Tooltip title="Ver detalles">
    //       <Button
    //         type="link"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewDetails(record)}
    //       />
    //     </Tooltip>
    //   )
    // }
  ];

const columns = [
    {
      title: 'Habitación',
      dataIndex: 'NumHab',
      key: 'NumHab',
      width: 100,
      fixed: 'left',
      sorter: (a, b) => a.NumHab - b.NumHab,
      render: (numHab) => (
        <Tag 
          color="blue" 
          style={{ 
            fontSize: '16px', 
            fontWeight: 'bold', 
            padding: '4px 12px',
            borderRadius: '20px'
          }}
        >
          <HomeOutlined style={{ marginRight: 6 }} />
          {numHab}
        </Tag>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': { label: 'Individual', color: 'cyan', icon: '👤' },
          'DO': { label: 'Doble', color: 'green', icon: '👥' },
          'TR': { label: 'Triple', color: 'orange', icon: '👥👤' },
          'CU': { label: 'Cuádruple', color: 'purple', icon: '👥👥' },
          'QU': { label: 'Quíntuple', color: 'red', icon: '👥👥👤' }
        };
        const config = tipos[tipo] || { label: tipo, color: 'default', icon: '🏠' };
        return (
          <Tag color={config.color} style={{ padding: '4px 10px', borderRadius: '16px' }}>
            {config.icon} {config.label}
          </Tag>
        );
      },
      filters: [
        { text: 'Individuales', value: 'IN' },
        { text: 'Dobles', value: 'DO' },
        { text: 'Triples', value: 'TR' },
        { text: 'Cuádruples', value: 'CU' },
        { text: 'Quíntuples', value: 'QU' }
      ],
      onFilter: (value, record) => record.TipoHab === value
    },
    {
      title: 'SubTipo',
      dataIndex: 'SubTipo',
      key: 'SubTipo',
      width: 100,
      render: (subtipo) => subtipo || '—'
    },
    {
      title: 'Descripción',
      dataIndex: 'lkDescrip',
      key: 'lkDescrip',
      width: 200,
      ellipsis: true,
      render: (descrip) => descrip || '—'
    },
    {
      title: 'Desde',
      dataIndex: 'desde',
      key: 'desde',
      width: 110,
      render: (desde) => desde ? dayjs(desde).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.desde).unix() - dayjs(b.desde).unix()
    },
    {
      title: 'Hasta',
      dataIndex: 'Hasta',
      key: 'Hasta',
      width: 110,
      render: (hasta) => hasta ? dayjs(hasta).format('DD/MM/YYYY') : '—',
      sorter: (a, b) => dayjs(a.Hasta).unix() - dayjs(b.Hasta).unix()
    },
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 100,
      align: 'center',
      sorter: (a, b) => a.Dias - b.Dias,
      render: (dias) => (
        <Badge 
          count={dias} 
          style={{ 
            backgroundColor: dias > 30 ? '#52c41a' : dias > 7 ? '#1890ff' : '#faad14',
            borderRadius: '12px',
            padding: '0 8px'
          }}
          showZero
        />
      )
    },
    {
      title: 'Días Libres (Total)',
      dataIndex: 'DiasLibres',
      key: 'DiasLibres',
      width: 120,
      align: 'center',
      render: (dias) => `${dias} días`
    },
    // {
    //   title: 'Acciones',
    //   key: 'actions',
    //   width: 80,
    //   fixed: 'right',
    //   render: (_, record) => (
    //     <Tooltip title="Ver detalles">
    //       <Button
    //         type="link"
    //         icon={<EyeOutlined />}
    //         onClick={() => handleViewDetails(record)}
    //       />
    //     </Tooltip>
    //   )
    // }
  ];  
  
  // Columnas para vista resumida
  const columnsResumidas = [
    {
      title: 'Días Libres',
      dataIndex: 'Dias',
      key: 'Dias',
      width: 100,
      render: (dias) => (
        <Tag color="blue" style={{ fontSize: '14px', fontWeight: 'bold' }}>
          {dias} {dias === 1 ? 'día' : 'días'}
        </Tag>
      )
    },
    {
      title: 'Habitaciones',
      dataIndex: 'Habitaciones',
      key: 'Habitaciones',
      render: (habitaciones) => (
        <Space wrap size="small">
          {habitaciones?.split(',').map((hab, idx) => (
            <Tag key={idx} color="cyan" style={{ fontSize: '13px' }}>
              {hab.trim()}
            </Tag>
          ))}
        </Space>
      )
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoHab',
      key: 'TipoHab',
      width: 120,
      render: (tipo) => {
        const tipos = {
          'IN': 'Individual',
          'DO': 'Doble',
          'TR': 'Triple',
          'CU': 'Cuádruple',
          'QU': 'Quíntuple'
        };
        return tipos[tipo] || tipo;
      }
    },
    {
      title: 'Total',
      dataIndex: 'Total',
      key: 'Total',
      width: 80,
      align: 'center',
      render: (total) => (
        <Badge 
          count={total} 
          style={{ backgroundColor: '#1890ff' }}
          showZero
        />
      )
    }
  ];
  
  // Cargar datos iniciales
  useEffect(() => {
    cargarEstadisticas();
    cargarRangoHabitaciones();
  }, []);
  
  const cargarEstadisticas = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones/estadisticas`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setEstadisticas(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando estadísticas:', error);
    }
  };

  const cargarRangoHabitaciones = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BASE_URL}/api/habitaciones/rango`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setRangoMinMax(response.data.data);
      }
    } catch (error) {
      console.error('Error cargando rango:', error);
    }
  };  
  
  const buscarHabitaciones = async () => {
    if (!fechas.desde || !fechas.hasta) {
      message.warning('Seleccione las fechas de búsqueda');
      return;
    }
    
    setSearching(true);
    setLoading(true);
    
    try {
      const token = localStorage.getItem('token');
      const params = {
        fechaDesde: fechas.desde.format('YYYY-MM-DD'),
        fechaHasta: fechas.hasta.format('YYYY-MM-DD'),
        tipoHab: filtroTipo !== 'TODAS' ? filtroTipo : '',
        habDesde: rangoHabs.desde,
        habHasta: rangoHabs.hasta,
        tipoResumen: vista === 'resumido' ? 'resumido' : 'detallado'
      };
      
      const response = await axios.get(`${BASE_URL}/api/habitaciones/libres`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        const data = response.data.data;
        
        if (vista === 'resumido') {
          // Procesar datos para vista resumida
          const resumen = {};
          data.forEach(item => {
            const key = `${item.Dias}_${item.TipoHab || 'TODAS'}`;
            if (!resumen[key]) {
              resumen[key] = {
                Dias: item.Dias,
                TipoHab: item.TipoHab || 'TODAS',
                Habitaciones: [],
                Total: 0
              };
            }
            resumen[key].Habitaciones.push(item.NumHab);
            resumen[key].Total++;
          });
          
          const resumenData = Object.values(resumen).map(item => ({
            ...item,
            Habitaciones: item.Habitaciones.sort((a, b) => a - b).join(', ')
          })).sort((a, b) => a.Dias - b.Dias);
          
          setHabitaciones(resumenData);
        } else {
          setHabitaciones(data);
        }
        
        // Calcular resumen por tipo de habitación
        const tiposResumen = {};
        data.forEach(item => {
          const tipo = item.TipoHab || 'OTROS';
          if (!tiposResumen[tipo]) {
            tiposResumen[tipo] = { total: 0, disponible: 0 };
          }
          tiposResumen[tipo].total++;
          if (item.Dias > 0) tiposResumen[tipo].disponible++;
        });
        
        setResumenTipos(Object.entries(tiposResumen).map(([tipo, stats]) => ({
          tipo,
          ...stats,
          porcentaje: stats.total > 0 ? Math.round((stats.disponible / stats.total) * 100) : 0
        })));
        
        // Calcular ocupación aproximada
        const totalHabs = data.length;
        const disponibles = data.filter(item => item.Dias > 0).length;
        setOcupacion(totalHabs > 0 ? Math.round(((totalHabs - disponibles) / totalHabs) * 100) : 0);
        
        message.success(`Se encontraron ${data.length} habitaciones`);
      }
    } catch (error) {
      console.error('Error buscando habitaciones:', error);
      message.error(error.response?.data?.error || 'Error al buscar habitaciones');
      setHabitaciones([]);
    } finally {
      setLoading(false);
      setSearching(false);
    }
  };
  
  const handleViewDetails = (record) => {
    setSelectedRow(record);
    setModalVisible(true);
  };
  
  const handleExport = async () => {
    try {
      message.loading('Generando exportación...', 1);
      
      const token = localStorage.getItem('token');
      const params = {
        fechaDesde: fechas.desde.format('YYYY-MM-DD'),
        fechaHasta: fechas.hasta.format('YYYY-MM-DD'),
        tipoHab: filtroTipo !== 'TODAS' ? filtroTipo : '',
        habDesde: rangoHabs.desde,
        habHasta: rangoHabs.hasta
      };
      
      const response = await axios.get(`${BASE_URL}/api/habitaciones/libres/export`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `habitaciones_libres_${dayjs().format('YYYYMMDD_HHmmss')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      message.success('Exportación completada');
    } catch (error) {
      console.error('Error exportando:', error);
      message.error('Error al exportar los datos');
    }
  };
  
  const handleImprimir = () => {
    const printWindow = window.open('', '_blank');
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Habitaciones Libres</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; }
          h1 { color: #1890ff; }
          .info { margin-bottom: 20px; padding: 10px; background: #f5f5f5; border-radius: 8px; }
          table { width: 100%; border-collapse: collapse; margin-top: 20px; }
          th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
          th { background-color: #1890ff; color: white; }
          tr:nth-child(even) { background-color: #f9f9f9; }
          .footer { margin-top: 30px; text-align: center; font-size: 12px; color: #666; }
        </style>
      </head>
      <body>
        <h1>🏨 Reporte de Habitaciones Libres</h1>
        <div class="info">
          <p><strong>Período:</strong> ${fechas.desde.format('DD/MM/YYYY')} - ${fechas.hasta.format('DD/MM/YYYY')}</p>
          <p><strong>Fecha de emisión:</strong> ${dayjs().format('DD/MM/YYYY HH:mm')}</p>
          <p><strong>Total habitaciones:</strong> ${habitaciones.length}</p>
        </div>
        <table>
          <thead>
            <tr><th>N° Habitación</th><th>Tipo</th><th>SubTipo</th><th>Desde</th><th>Hasta</th><th>Días Libres</th></tr>
          </thead>
          <tbody>
            ${habitaciones.filter(h => h.NumHab).map(h => `
              <tr>
                <td>${h.NumHab}</td>
                <td>${h.TipoHab || '—'}</td>
                <td>${h.SubTipo || '—'}</td>
                <td>${h.Desde ? dayjs(h.Desde).format('DD/MM/YYYY') : '—'}</td>
                <td>${h.Hasta ? dayjs(h.Hasta).format('DD/MM/YYYY') : '—'}</td>
                <td>${h.Dias || 0}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        <div class="footer">
          <p>Reporte generado por el Sistema de Gestión Hotelera</p>
        </div>
      </body>
      </html>
    `;
    
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    printWindow.print();
  };
  
  const handleReset = () => {
    setFechas({
      desde: dayjs(),
      hasta: dayjs().add(1, 'days')
    });
    setRangoHabs({ desde: '', hasta: '' });
    setFiltroTipo('TODAS');
    setTipoHabitacion('');
    setHabitaciones([]);
    setResumenTipos([]);
    setOcupacion(0);
  };
  
  // Componente de filtros de búsqueda
  const FiltrosBusqueda = () => (
    <Card 
      style={{ marginBottom: 24, borderRadius: 12 }}
      bodyStyle={{ padding: '20px' }}
    >
      <Row gutter={[24, 16]}>
        <Col xs={24} md={12} lg={8}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>
              <CalendarOutlined style={{ marginRight: 8 }} />
              Período de Consulta
            </Text>
          </div>
          <RangePicker
            value={[fechas.desde, fechas.hasta]}
            onChange={(dates) => {
              if (dates && dates[0] && dates[1]) {
                setFechas({ desde: dates[0], hasta: dates[1] });
              }
            }}
            style={{ width: '100%' }}
            format="DD/MM/YYYY"
            placeholder={['Fecha desde', 'Fecha hasta']}
          />
        </Col>
        
        <Col xs={24} md={12} lg={6}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Tipo de Habitación</Text>
          </div>
          <Select
            value={filtroTipo}
            onChange={setFiltroTipo}
            style={{ width: '100%' }}
            placeholder="Seleccionar tipo"
          >
            <Option value="TODAS">Todas</Option>
            <Option value="IN">Individuales</Option>
            <Option value="DO">Dobles</Option>
            <Option value="TR">Triples</Option>
            <Option value="CU">Cuádruples</Option>
            <Option value="QU">Quíntuples</Option>
          </Select>
        </Col>
        
        <Col xs={24} md={12} lg={5}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Habitación Desde</Text>
          </div>
          <Input
            type="number"
            placeholder="N° habitación"
            value={rangoHabs.desde}
            onChange={(e) => setRangoHabs({ ...rangoHabs, desde: e.target.value })}
            prefix={<HomeOutlined />}
          />
        </Col>
        
        <Col xs={24} md={12} lg={5}>
          <div style={{ marginBottom: 8 }}>
            <Text strong>Habitación Hasta</Text>
          </div>
          <Input
            type="number"
            placeholder="N° habitación"
            value={rangoHabs.hasta}
            onChange={(e) => setRangoHabs({ ...rangoHabs, hasta: e.target.value })}
            prefix={<HomeOutlined />}
          />
        </Col>
      </Row>
      
      <Divider style={{ margin: '16px 0' }} />
      
      <Row justify="end" gutter={16}>
        <Col>
          <Button onClick={handleReset} icon={<ReloadOutlined />}>
            Restablecer
          </Button>
        </Col>
        <Col>
          <Button 
            type="primary" 
            onClick={buscarHabitaciones} 
            loading={searching}
            icon={<SearchOutlined />}
            size="large"
          >
            Buscar Habitaciones
          </Button>
        </Col>
      </Row>
    </Card>
  );
  
  // Componente de estadísticas
  const EstadisticasPanel = () => (
    <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
      <Col xs={24} sm={12} lg={6}>
        <Card 
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title="Total Habitaciones"
            value={habitaciones.filter(h => h.NumHab).length}
            prefix={<HomeOutlined style={{ color: '#1890ff' }} />}
            valueStyle={{ color: '#1890ff' }}
          />
          <Progress 
            percent={100} 
            showInfo={false} 
            strokeColor="#1890ff" 
            style={{ marginTop: 8 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card 
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title="Ocupación Estimada"
            value={ocupacion}
            suffix="%"
            prefix={<BarChartOutlined style={{ color: '#faad14' }} />}
            valueStyle={{ color: ocupacion > 80 ? '#f5222d' : '#faad14' }}
          />
          <Progress 
            percent={ocupacion} 
            strokeColor={ocupacion > 80 ? '#f5222d' : '#faad14'}
            style={{ marginTop: 8 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card 
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title="Habitaciones Disponibles"
            value={habitaciones.filter(h => h.Dias > 0 && h.NumHab).length}
            prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
            valueStyle={{ color: '#52c41a' }}
          />
          <Progress 
            percent={habitaciones.length > 0 
              ? (habitaciones.filter(h => h.Dias > 0 && h.NumHab).length / habitaciones.filter(h => h.NumHab).length) * 100 
              : 0
            } 
            strokeColor="#52c41a"
            style={{ marginTop: 8 }}
          />
        </Card>
      </Col>
      <Col xs={24} sm={12} lg={6}>
        <Card 
          style={{ borderRadius: 12 }}
          bodyStyle={{ padding: '20px' }}
        >
          <Statistic
            title="Promedio Días Libres"
            value={habitaciones.length > 0 
              ? Math.round(habitaciones.filter(h => h.Dias).reduce((sum, h) => sum + (h.Dias || 0), 0) / habitaciones.filter(h => h.NumHab).length)
              : 0
            }
            suffix="días"
            prefix={<CalendarOutlined style={{ color: '#722ed1' }} />}
            valueStyle={{ color: '#722ed1' }}
          />
          <Progress 
            percent={habitaciones.length > 0 
              ? (habitaciones.filter(h => h.Dias).reduce((sum, h) => sum + (h.Dias || 0), 0) / (habitaciones.filter(h => h.NumHab).length * 30)) * 100
              : 0
            }
            strokeColor="#722ed1"
            style={{ marginTop: 8 }}
          />
        </Card>
      </Col>
    </Row>
  );
  
  // Componente de resumen por tipo de habitación
  const ResumenTiposPanel = () => {
    if (resumenTipos.length === 0) return null;
    
    return (
      <Card 
        title={
          <span>
            <UnorderedListOutlined style={{ marginRight: 8 }} />
            Resumen por Tipo de Habitación
          </span>
        }
        style={{ marginBottom: 24, borderRadius: 12 }}
      >
        <Row gutter={[16, 16]}>
          {resumenTipos.map((item) => (
            <Col xs={24} sm={12} md={8} lg={4} key={item.tipo}>
              <Card 
                size="small"
                style={{ textAlign: 'center', borderRadius: 10 }}
                bodyStyle={{ padding: '12px' }}
              >
                <Text strong>{item.tipo}</Text>
                <div style={{ marginTop: 8 }}>
                  <Badge count={item.disponible} style={{ backgroundColor: '#52c41a' }} />
                  <Text type="secondary"> / {item.total}</Text>
                </div>
                <Progress 
                  percent={item.porcentaje} 
                  size="small"
                  strokeColor={item.porcentaje > 50 ? '#52c41a' : '#faad14'}
                  style={{ marginTop: 8 }}
                />
                <Text type="secondary" style={{ fontSize: 12 }}>
                  {item.porcentaje}% disponible
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };
  
  // Vista de tabla
  const VistaTabla = () => (
    <Table
      columns={columns}
      dataSource={habitaciones.filter(h => h.NumHab)}
      rowKey={(record) => record.NumHab || record.Dias + '_' + record.TipoHab}
      loading={loading}
      scroll={{ x: 1000, y: 500 }}
      pagination={{
        pageSize: 15,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} habitaciones`,
        pageSizeOptions: ['10', '15', '20', '50']
      }}
      size="middle"
      rowClassName={(record) => 
        record.Dias === 0 ? 'ant-table-row-disabled' : ''
      }
    />
  );
  
  // Vista resumida
  const VistaResumida = () => (
    <Table
      columns={columnsResumidas}
      dataSource={habitaciones}
      rowKey={(record) => `${record.Dias}_${record.TipoHab}`}
      loading={loading}
      scroll={{ x: 600 }}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        showTotal: (total, range) => `${range[0]}-${range[1]} de ${total} grupos`
      }}
    />
  );
  
  return (
    <>
      <PageContainer
        title="Habitaciones Libres"
        subtitle="Consulta de disponibilidad de habitaciones por período"
        icon={<HomeOutlined />}
        loading={loading}
        onRefresh={buscarHabitaciones}
        // badgeCount={habitaciones.length}
        // badgeStatus="processing"
        extra={
          <Space>
            {/* <Segmented
              value={vista}
              onChange={setVista}
              options={[
                { value: 'tabla', icon: <TableOutlined />, label: 'Detallado' },
                { value: 'resumido', icon: <UnorderedListOutlined />, label: 'Resumido' }
              ]}
            /> */}
            <Tooltip title="Exportar a Excel">
              <Button
                icon={<ExportOutlined />}
                onClick={handleExport}
                disabled={habitaciones.length === 0}
              />
            </Tooltip>
            <Tooltip title="Imprimir Reporte">
              <Button
                icon={<PrinterOutlined />}
                onClick={handleImprimir}
                disabled={habitaciones.length === 0}
              />
            </Tooltip>
          </Space>
        }
      >
        {/* Filtros de búsqueda */}
        <FiltrosBusqueda />
        
        {/* Estadísticas */}
        {habitaciones.length > 0 && (
          <>
            {/* <EstadisticasPanel /> */}
            {/* <ResumenTiposPanel /> */}
          </>
        )}
        
        {/* Resultados */}
        {habitaciones.length > 0 ? (
          vista === 'tabla' ? <VistaTabla /> : <VistaResumida />
        ) : (
          !searching && (
            <Empty
              description={
                <span>
                  No hay datos para mostrar
                  <br />
                  <Text type="secondary">Seleccione fechas y presione "Buscar Habitaciones"</Text>
                </span>
              }
              style={{ marginTop: 50 }}
            />
          )
        )}
        
        {/* Loading overlay */}
        {searching && (
          <div style={{ textAlign: 'center', padding: '50px' }}>
            <Spin size="large" tip="Buscando habitaciones disponibles..." />
          </div>
        )}
      </PageContainer>
      
      {/* Modal de detalles */}
      <Modal
        title={
          <span>
            <HomeOutlined style={{ marginRight: 8 }} />
            Detalles de Habitación {selectedRow?.NumHab}
          </span>
        }
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setModalVisible(false)}>
            Cerrar
          </Button>,
          <Button key="print" type="primary" icon={<PrinterOutlined />} onClick={handleImprimir}>
            Imprimir
          </Button>
        ]}
        width={600}
      >
        {selectedRow && (
          <Descriptions column={2} bordered size="small">
            <Descriptions.Item label="N° Habitación" span={2}>
              <Tag color="blue" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                {selectedRow.NumHab}
              </Tag>
            </Descriptions.Item>
            <Descriptions.Item label="Tipo">
              {selectedRow.TipoHab}
            </Descriptions.Item>
            <Descriptions.Item label="SubTipo">
              {selectedRow.SubTipo || '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Desde">
              {selectedRow.Desde ? dayjs(selectedRow.Desde).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Fecha Hasta">
              {selectedRow.Hasta ? dayjs(selectedRow.Hasta).format('DD/MM/YYYY') : '—'}
            </Descriptions.Item>
            <Descriptions.Item label="Días Libres">
              <Badge 
                count={selectedRow.Dias} 
                style={{ 
                  backgroundColor: selectedRow.Dias > 30 ? '#52c41a' : '#1890ff',
                  borderRadius: '12px',
                  padding: '0 8px'
                }}
              />
            </Descriptions.Item>
            <Descriptions.Item label="Estado">
              {selectedRow.Dias > 0 ? (
                <Tag color="success" icon={<CheckCircleOutlined />}>Disponible</Tag>
              ) : (
                <Tag color="error" icon={<CloseCircleOutlined />}>Ocupada</Tag>
              )}
            </Descriptions.Item>
            {selectedRow.Txt && (
              <Descriptions.Item label="Observaciones" span={2}>
                <Alert message={selectedRow.Txt} type="info" showIcon />
              </Descriptions.Item>
            )}
          </Descriptions>
        )}
      </Modal>
    </>
  );
};

export default HabitacionesLibres;